import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, already_verified
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please check your email.');
        return;
      }

      try {
        const response = await fetch(`/api/auth/verify-email?token=${token}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok) {
          if (data.already_verified) {
            setStatus('already_verified');
            setMessage('Your email was already verified. You can proceed to the app.');
          } else {
            setStatus('success');
            setMessage('Your email has been verified successfully! Welcome to Constant Love™ 💕');
          }
          
          // Redirect to onboarding after 3 seconds
          setTimeout(() => {
            navigate('/onboarding');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed. The link may have expired.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('An error occurred during verification. Please try again.');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md p-8 bg-white/80 backdrop-blur-sm border-rose-200 shadow-xl text-center">
        <div className="flex justify-center mb-6">
          <img 
            src="/constant-love-logo.png" 
            alt="Constant Love™" 
            className="w-32 h-32 object-contain"
          />
        </div>

        {status === 'verifying' && (
          <>
            <Loader className="w-16 h-16 text-rose-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email...</h2>
            <p className="text-gray-600">Please wait while we confirm your email address.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified! 🎉</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="bg-rose-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700">
                Redirecting you to your dashboard in 3 seconds...
              </p>
            </div>
            <Button 
              asChild
              className="w-full bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-full py-3"
            >
              <Link to="/app">Go to Dashboard Now →</Link>
            </Button>
          </>
        )}

        {status === 'already_verified' && (
          <>
            <CheckCircle className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Already Verified ✓</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <Button 
              asChild
              className="w-full bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-full py-3"
            >
              <Link to="/app">Go to Dashboard →</Link>
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Link expired?</strong> Log in to your account and request a new verification email from your dashboard.
              </p>
            </div>
            <div className="space-y-3">
              <Button 
                asChild
                className="w-full bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-full py-3"
              >
                <Link to="/login">Go to Login →</Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="w-full border-rose-300 text-rose-600 hover:bg-rose-50 font-bold rounded-full py-3"
              >
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default VerifyEmail;
